import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Effect, Match } from "effect";
import { Trash2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

import {
  ApiError,
  Credential,
  type StaffAccountSummary,
} from "#api.ts";
import { useAuth } from "#auth.tsx";
import { runEffectMutation, runEffectQuery } from "#effect-query.ts";
import { StaffDialog } from "#order-dialog.tsx";
import { useLanguage } from "#i18n.tsx";

const kitchenStaffQueryKey = ["settings", "kitchen-staff"] as const;
type CreateKitchenStaffInput = {
  readonly username: string;
};
type KitchenShiftQr = {
  readonly account: StaffAccountSummary;
  readonly token: Credential;
  readonly handoffExpiresAt: number;
  readonly shiftExpiresAt: number;
};

/** Administrator control for creating least-privilege kitchen identities. */
export function StaffAccountSettings() {
  const { api } = useAuth();
  const { language, t } = useLanguage();
  const queryClient = useQueryClient();
  const form = useRef<HTMLFormElement>(null);
  const [notice, setNotice] = useState<string>();
  const [shiftQr, setShiftQr] = useState<KitchenShiftQr>();
  const [removingAccount, setRemovingAccount] =
    useState<StaffAccountSummary>();
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!shiftQr) return;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= shiftQr.handoffExpiresAt) {
        clearInterval(timer);
        setShiftQr(undefined);
      }
    }, 1_000);

    return () => clearInterval(timer);
  }, [shiftQr]);
  const accounts = useQuery({
    queryKey: kitchenStaffQueryKey,
    retry: false,
    queryFn: ({ signal }) =>
      runEffectQuery(
        Effect.promise(() => api.listKitchenStaff()).pipe(
          Effect.flatMap((result) =>
            result.ok ? Effect.succeed(result.value) : Effect.fail(result.error),
          ),
          Effect.withSpan("StaffAccountSettings.list"),
        ),
        signal,
      ),
  });
  const create = useMutation({
    retry: false,
    mutationFn: (input: CreateKitchenStaffInput) =>
      runEffectMutation(
        Effect.promise(() => api.createKitchenStaff(input)).pipe(
          Effect.flatMap((result) =>
            result.ok ? Effect.succeed(result.value) : Effect.fail(result.error),
          ),
          Effect.withSpan("StaffAccountSettings.create"),
        ),
      ),
    onSuccess: (account) => {
      form.current?.reset();
      create.reset();
      setNotice(
        `${account.username} ${t("staff.ready")}`,
      );
      void queryClient.invalidateQueries({ queryKey: kitchenStaffQueryKey });
    },
  });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotice(undefined);
    create.reset();
    const fields = new FormData(event.currentTarget);
    create.mutate({
      username: String(fields.get("username") ?? "").trim(),
    });
  };
  const startShift = useMutation({
    retry: false,
    mutationFn: (account: StaffAccountSummary) =>
      runEffectMutation(
        Effect.promise(() => api.createKitchenShift(account.id)).pipe(
          Effect.flatMap((result) =>
            result.ok ? Effect.succeed(result.value) : Effect.fail(result.error),
          ),
          Effect.withSpan("StaffAccountSettings.createKitchenShift"),
        ),
      ),
    onSuccess: (shift, account) => {
      setNow(Date.now());
      setShiftQr({ account, ...shift });
    },
  });
  const remove = useMutation({
    retry: false,
    mutationFn: (account: StaffAccountSummary) =>
      runEffectMutation(
        Effect.promise(() => api.deleteKitchenStaff(account.id)).pipe(
          Effect.flatMap((result) =>
            result.ok ? Effect.succeed(undefined) : Effect.fail(result.error),
          ),
          Effect.withSpan("StaffAccountSettings.removeKitchenStaff"),
        ),
      ),
    onSuccess: (_, account) => {
      if (shiftQr?.account.id === account.id) setShiftQr(undefined);
      setRemovingAccount(undefined);
      setNotice(`${account.username} ${t("staff.removed")}`);
      void queryClient.invalidateQueries({ queryKey: kitchenStaffQueryKey });
    },
  });
  const activeShiftQr =
    shiftQr && shiftQr.handoffExpiresAt > now ? shiftQr : undefined;

  return (
    <section className="settings-card">
      <h2>{t("staff.title")}</h2>
      <p>{t("staff.description")}</p>
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}
      {create.error && (
        <p className="alert" role="alert">
          {create.error.message}
        </p>
      )}
      <form ref={form} className="staff-account-form" onSubmit={submit}>
        <label>
          {t("staff.username")}
          <input
            name="username"
            placeholder="e.g. tomomi"
            minLength={3}
            maxLength={64}
            pattern="[A-Za-z0-9._-]+"
            autoCapitalize="none"
            autoCorrect="off"
            required
          />
        </label>
        <button className="primary" type="submit" disabled={create.isPending}>
          {create.isPending ? t("staff.creating") : t("staff.create")}
        </button>
      </form>
      {startShift.error && (
        <p className="alert" role="alert">
          {startShift.error.message}
        </p>
      )}
      {Match.value(accounts).pipe(
        Match.when({ status: "pending" }, () => (
          <p className="muted" role="status">
            {t("staff.loading")}
          </p>
        )),
        Match.when({ status: "error" }, ({ error }) =>
          Match.valueTags(error as ApiError, {
            ApiError: (failure) => (
              <p className="alert" role="alert">
                {failure.message}
              </p>
            ),
          }),
        ),
        Match.when({ status: "success" }, ({ data }) =>
          data.length === 0 ? (
            <p className="muted">{t("staff.none")}</p>
          ) : (
            <ul className="passkey-list staff-account-list">
              {data.map((account) => (
                <li className="staff-account-item" key={account.id}>
                  <div className="staff-account-summary">
                    <span>
                      <strong>{account.username}</strong>
                    </span>
                    <div className="staff-account-actions">
                      <span className="pill">{t("staff.role")}</span>
                      {!activeShiftQr && (
                        <button
                          className="secondary"
                          type="button"
                          disabled={startShift.isPending}
                          onClick={() => {
                            setNotice(undefined);
                            startShift.reset();
                            startShift.mutate(account);
                          }}
                        >
                          {startShift.isPending &&
                          startShift.variables?.id === account.id
                            ? t("staff.creatingQr")
                            : t("staff.startShift")}
                        </button>
                      )}
                      <button
                        className="secondary danger staff-account-remove"
                        type="button"
                        disabled={remove.isPending}
                        onClick={() => {
                          remove.reset();
                          setRemovingAccount(account);
                        }}
                      >
                        <Trash2 aria-hidden="true" />
                        {t("common.remove")}
                      </button>
                    </div>
                  </div>
                  {activeShiftQr?.account.id === account.id && (
                    <div className="staff-shift-qr tracking-qr">
                      <QRCodeSVG
                        size={220}
                        value={`${window.location.origin}/login#one-time-token=${encodeURIComponent(activeShiftQr.token.reveal())}`}
                      />
                      <p>
                        {t("staff.scanOnce")} {" "}
                        {Math.max(
                          1,
                          Math.ceil(
                            (activeShiftQr.handoffExpiresAt - now) / 60_000,
                          ),
                        )}{" "}
                        {t("staff.minutes")}
                      </p>
                      <p className="muted">
                        {activeShiftQr.account.username} {t("staff.accessUntil")} {" "}
                        {new Date(
                          activeShiftQr.shiftExpiresAt,
                        ).toLocaleTimeString(language, {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                        .
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          ),
        ),
        Match.exhaustive,
      )}
      {removingAccount && (
        <StaffDialog
          title={`${t("staff.removeTitle")} ${removingAccount.username}?`}
          closeLabel={t("staff.removeClose")}
          presentation="confirmation"
          close={() => {
            if (!remove.isPending) setRemovingAccount(undefined);
          }}
        >
          <div className="dialog-body">
            <p>{t("staff.removeWarning")}</p>
            {remove.error && (
              <p className="alert" role="alert">
                {remove.error.message}
              </p>
            )}
          </div>
          <footer className="dialog-footer">
            <button
              className="secondary"
              type="button"
              disabled={remove.isPending}
              onClick={() => setRemovingAccount(undefined)}
            >
              {t("staff.keep")}
            </button>
            <button
              className="primary danger"
              type="button"
              disabled={remove.isPending}
              onClick={() => remove.mutate(removingAccount)}
            >
              {remove.isPending ? t("staff.removing") : t("staff.remove")}
            </button>
          </footer>
        </StaffDialog>
      )}
    </section>
  );
}
